import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  paragraphsOrPrompt,
  parseWholeCount,
  splitItems,
  splitLeadIn,
  splitLines,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A research proposal outline: the seven sections most programmes ask for, each with a
 * word target from a typical split of the total, the writer's own problem, questions,
 * gap, methods, ethics, timeline and outcomes placed in them, and methodology prompts
 * that follow the chosen approach (quantitative, qualitative or mixed methods).
 */

export type ProposalApproach =
  | "quantitative"
  | "qualitative"
  | "mixed"
  | "undecided";

export interface ResearchProposalInput {
  title: string;
  problem: string;
  /** One research question per line. */
  questions: string;
  rationale: string;
  approach: ProposalApproach;
  methodology: string;
  ethics: string;
  /** One milestone per line: "Months 1–3: Literature review". */
  timeline: string;
  outcomes: string;
  /** The target length in words. */
  words: string;
}

export const PROPOSAL_SECTIONS = [
  "introduction",
  "literature",
  "questions",
  "methodology",
  "ethics",
  "timeline",
  "outcomes",
] as const;

export type ProposalSection = (typeof PROPOSAL_SECTIONS)[number];

/**
 * A typical split, in percent — a planning aid rather than a standard. Methodology and
 * the literature carry most of the weight because that is where reviewers decide whether
 * the study can be done and is worth doing.
 */
export const PROPOSAL_SHARES: Record<ProposalSection, number> = {
  introduction: 15,
  literature: 25,
  questions: 10,
  methodology: 30,
  ethics: 5,
  timeline: 5,
  outcomes: 10,
};

export const MIN_PROPOSAL_WORDS = 100;
export const MAX_PROPOSAL_WORDS = 100_000;

/**
 * Split a total into word targets rounded to `step`, so that the targets add up exactly
 * to the total rounded to `step`. Rounding each share on its own does not: 15%, 25%…
 * of 2,500 rounded separately can sum to 2,510. Largest remainder fixes that — floor
 * every share, then hand the units left over to the shares that lost the most; ties go
 * to the earlier section.
 */
export function allocateWords(
  total: number,
  shares: readonly number[],
  step = 10
): number[] {
  const units = Math.round(total / step);
  const shareSum = shares.reduce((sum, share) => sum + share, 0);
  if (units <= 0 || shareSum <= 0) {
    return shares.map(() => 0);
  }
  const exact = shares.map((share) => (units * share) / shareSum);
  const allocated = exact.map((value) => Math.floor(value));
  let left = units - allocated.reduce((sum, value) => sum + value, 0);
  const byRemainder = exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const { index } of byRemainder) {
    if (left <= 0) {
      break;
    }
    allocated[index] += 1;
    left -= 1;
  }
  return allocated.map((value) => value * step);
}

/** The target length, or null when it is missing or outside a sensible range. */
export function proposalWordTotal(raw: string): number | null {
  const total = parseWholeCount(raw);
  if (
    total === null ||
    total < MIN_PROPOSAL_WORDS ||
    total > MAX_PROPOSAL_WORDS
  ) {
    return null;
  }
  return total;
}

/**
 * "Months 1–3: Literature review" → ["Months 1–3", "Literature review"], one row per
 * line. "Weeks 4 - 6: Interviews" splits at the colon, not the dash. A line with no
 * separator has no period: ["", line].
 */
export function parseTimeline(text: string): [string, string][] {
  return splitItems(text).map((line) => splitLeadIn(line) ?? ["", line]);
}

type ApproachPrompts = Record<ProposalApproach, string>;

export interface ResearchProposalLabels {
  defaultTitle: string;
  subtitle: string;
  subtitleWords: string;
  budgetHeading: string;
  budgetNote: string;
  budget: { section: string; share: string; words: string; total: string };
  sections: Record<ProposalSection, string> & { references: string };
  target: string;
  targetShare: string;
  tips: Record<ProposalSection, string> & { references: string };
  subheadings: {
    problem: string;
    gap: string;
    design: string;
    sample: string;
    collection: string;
    analysis: string;
    limitations: string;
  };
  prompts: {
    problem: string;
    context: string;
    literature: string;
    gap: string;
    questions: string;
    limitations: string;
    ethics: string;
    timeline: string;
    outcomes: string;
    references: string;
  };
  design: ApproachPrompts;
  sample: ApproachPrompts;
  collection: ApproachPrompts;
  analysis: ApproachPrompts;
  rqLabel: string;
  ethicsChecklist: readonly string[];
  timelineHead: { period: string; activity: string };
}

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

const PERCENT: Intl.NumberFormatOptions = { style: "percent" };

function percent(fmt: Format, section: ProposalSection): string {
  return fmt(PROPOSAL_SHARES[section] / 100, PERCENT);
}

function budgetBlock(
  labels: ResearchProposalLabels,
  targets: Record<ProposalSection, number> | null,
  fmt: Format
): DocBlock {
  const rows = PROPOSAL_SECTIONS.map((section) => {
    const row = [labels.sections[section], percent(fmt, section)];
    return targets ? [...row, fmt(targets[section])] : row;
  });
  const sum = targets
    ? Object.values(targets).reduce((total, value) => total + value, 0)
    : 0;
  const totalRow = [labels.budget.total, fmt(1, PERCENT)];
  const head = [labels.budget.section, labels.budget.share];
  return {
    kind: "table",
    head: targets ? [...head, labels.budget.words] : head,
    rows: [...rows, targets ? [...totalRow, fmt(sum)] : totalRow],
  };
}

function targetGuidance(
  section: ProposalSection,
  labels: ResearchProposalLabels,
  targets: Record<ProposalSection, number> | null,
  fmt: Format
): DocBlock {
  const target = targets
    ? t(labels.target, {
        words: fmt(targets[section]),
        share: percent(fmt, section),
      })
    : t(labels.targetShare, { share: percent(fmt, section) });
  return { kind: "guidance", text: `${target} ${labels.tips[section]}` };
}

function questionsBlocks(
  input: ResearchProposalInput,
  labels: ResearchProposalLabels
): DocBlock[] {
  const questions = splitItems(input.questions);
  if (questions.length === 0) {
    return [{ kind: "prompt", text: labels.prompts.questions }];
  }
  return [
    {
      kind: "list",
      list: {
        style: "bullet",
        items: questions.map((question, index) => ({
          text: [
            { text: t(labels.rqLabel, { n: index + 1 }), bold: true },
            { text: ` ${question}` },
          ],
        })),
      },
    },
  ];
}

function methodologyBlocks(
  input: ResearchProposalInput,
  labels: ResearchProposalLabels
): DocBlock[] {
  const approach = input.approach;
  const notes: DocBlock[] = splitLines(input.methodology).map((text) => ({
    kind: "paragraph",
    text,
  }));
  return [
    ...notes,
    { kind: "subheading", text: labels.subheadings.design },
    { kind: "prompt", text: labels.design[approach] },
    { kind: "subheading", text: labels.subheadings.sample },
    { kind: "prompt", text: labels.sample[approach] },
    { kind: "subheading", text: labels.subheadings.collection },
    { kind: "prompt", text: labels.collection[approach] },
    { kind: "subheading", text: labels.subheadings.analysis },
    { kind: "prompt", text: labels.analysis[approach] },
    { kind: "subheading", text: labels.subheadings.limitations },
    { kind: "prompt", text: labels.prompts.limitations },
  ];
}

function timelineBlocks(
  input: ResearchProposalInput,
  labels: ResearchProposalLabels
): DocBlock[] {
  const rows = parseTimeline(input.timeline);
  if (rows.length === 0) {
    return [{ kind: "prompt", text: labels.prompts.timeline }];
  }
  if (rows.every(([period]) => period === "")) {
    return [
      {
        kind: "list",
        list: {
          style: "decimal",
          items: rows.map(([, activity]) => ({ text: activity })),
        },
      },
    ];
  }
  return [
    {
      kind: "table",
      head: [labels.timelineHead.period, labels.timelineHead.activity],
      rows,
    },
  ];
}

function sectionBody(
  section: ProposalSection,
  input: ResearchProposalInput,
  labels: ResearchProposalLabels
): DocBlock[] {
  switch (section) {
    case "introduction":
      return [
        { kind: "subheading", text: labels.subheadings.problem },
        ...paragraphsOrPrompt(input.problem, labels.prompts.problem),
        { kind: "prompt", text: labels.prompts.context },
      ];
    case "literature":
      return [
        { kind: "prompt", text: labels.prompts.literature },
        { kind: "subheading", text: labels.subheadings.gap },
        ...paragraphsOrPrompt(input.rationale, labels.prompts.gap),
      ];
    case "questions":
      return questionsBlocks(input, labels);
    case "methodology":
      return methodologyBlocks(input, labels);
    case "ethics":
      return [
        ...paragraphsOrPrompt(input.ethics, labels.prompts.ethics),
        {
          kind: "checklist",
          items: labels.ethicsChecklist.map((text) => ({ text })),
        },
      ];
    case "timeline":
      return timelineBlocks(input, labels);
    default:
      return paragraphsOrPrompt(input.outcomes, labels.prompts.outcomes);
  }
}

export function buildResearchProposal(
  input: ResearchProposalInput,
  labels: ResearchProposalLabels,
  fmt: Format
): DocumentModel {
  const total = proposalWordTotal(input.words);
  const allocation = total
    ? allocateWords(
        total,
        PROPOSAL_SECTIONS.map((section) => PROPOSAL_SHARES[section])
      )
    : null;
  const targets = allocation
    ? (Object.fromEntries(
        PROPOSAL_SECTIONS.map((section, index) => [section, allocation[index]])
      ) as Record<ProposalSection, number>)
    : null;
  const sections: DocSection[] = PROPOSAL_SECTIONS.map((section) => ({
    heading: labels.sections[section],
    blocks: [
      targetGuidance(section, labels, targets, fmt),
      ...sectionBody(section, input, labels),
    ],
  }));
  return {
    title: input.title.trim() || labels.defaultTitle,
    subtitle: total
      ? t(labels.subtitleWords, { words: fmt(total) })
      : labels.subtitle,
    sections: [
      {
        heading: labels.budgetHeading,
        blocks: [
          { kind: "guidance", text: labels.budgetNote },
          budgetBlock(labels, targets, fmt),
        ],
      },
      ...sections,
      {
        heading: labels.sections.references,
        blocks: [
          { kind: "guidance", text: labels.tips.references },
          { kind: "prompt", text: labels.prompts.references },
        ],
      },
    ],
  };
}
